import 'rxjs/add/observable/empty';
import findIndex from 'lodash/findIndex';
import { Observable } from 'rxjs/Observable';
import type { Subscription } from 'rxjs/Subscription';
import type { StoreActionApi } from '@atlassian/react-sweet-state';
import { NEW_GROUP_ID } from '../../../../common/constants';
import type { Group } from '../../../../common/types';
import reorderGroupsRest from '../../../reorder-groups';
import { getGroupsSelector } from '../../selectors';
import type { ImmutableState, ContainerProps } from '../../types';
import type { ActionCallback } from '../types';
import { addRevertFlagToErrorResponse, reorderGroupsMutator } from '../utils';

const reorderGroups =
	(from: number, to: number, callback: ActionCallback<Group[]>) =>
	(
		{ setState, getState }: StoreActionApi<ImmutableState>,
		{ projectId, activeStreams }: ContainerProps,
	): Subscription => {
		const oldState = getState();
		const templateIndex = findIndex(getGroupsSelector(oldState), { id: NEW_GROUP_ID });
		const hasGroupTemplate = templateIndex >= 0;

		setState(reorderGroupsMutator(from, to, oldState));
		const updatedState = getState();
		const newOrder = getGroupsSelector(updatedState).map((group) => group.id);

		if (hasGroupTemplate) {
			// Is the template being moved around
			const isMovingTemplate = templateIndex === from;
			// If we are just changing places with template
			const isSwappingWithTempate = templateIndex === to && newOrder[from] === NEW_GROUP_ID;

			if (isMovingTemplate || isSwappingWithTempate) {
				return Observable.empty<never>().subscribe();
			}

			// Indexes can only shift one place up or down when moving so we can start
			// searching from template position -1
			const newTemplateIndex = newOrder.indexOf(NEW_GROUP_ID, Math.max(templateIndex - 1, 0));
			newOrder.splice(newTemplateIndex, 1);
		}

		const subscription = reorderGroupsRest(projectId, newOrder).subscribe(callback, (error) => {
			const canRevertState = updatedState === getState();

			if (canRevertState) {
				setState(oldState);
			}
			callback(undefined, addRevertFlagToErrorResponse(error, canRevertState));
		});

		activeStreams && activeStreams.add(subscription);

		return subscription;
	};

export default reorderGroups;
